import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, Plane, Heart, FileText, GraduationCap, Home, CreditCard, Shield, ChevronDown } from 'lucide-react';
import { usePageMeta } from '@/hooks/useSEO';

// ─── Cost Data Matrix ───
const costData = {
  germany: {
    name: 'Germany', flag: 'DE', currency: 'EUR', currencySymbol: '\u20AC', inrRate: 90,
    tuition: {
      public:  { bachelors: 500,   masters: 500,   mba: 15000 },
      private: { bachelors: 12000, masters: 15000, mba: 25000 },
    },
    living: 11208, // Blocked account requirement per year
    livingLabel: 'Blocked Account (mandatory)',
    visa: 75,
    insurance: 1200,
    flight: 500,
    extras: [
      { label: 'APS Certificate', cost: 200 },
      { label: 'Semester Contribution (Public)', cost: 350 },
      { label: 'Language Course (optional)', cost: 800 },
    ],
    note: 'Public university tuition is ~250 EUR/semester (admin fees only). MBA programs at private institutions are significantly higher.',
  },
  uk: {
    name: 'United Kingdom', flag: 'GB', currency: 'GBP', currencySymbol: '\u00A3', inrRate: 106,
    tuition: {
      public:  { bachelors: 14000, masters: 18000, mba: 30000 },
      private: { bachelors: 18000, masters: 24000, mba: 45000 },
    },
    living: 12006, // Maintenance funds London rate ~1015/month
    livingLabel: 'Maintenance Funds (visa requirement)',
    visa: 490,
    insurance: 776, // IHS surcharge per year
    flight: 550,
    extras: [
      { label: 'IHS Surcharge', cost: 776 },
      { label: 'TB Test Certificate', cost: 80 },
      { label: 'CAS Deposit', cost: 2000 },
    ],
    note: 'UK masters are typically 1 year. IHS (Immigration Health Surcharge) is mandatory. Tuition varies widely by university ranking.',
  },
  canada: {
    name: 'Canada', flag: 'CA', currency: 'CAD', currencySymbol: 'C$', inrRate: 62,
    tuition: {
      public:  { bachelors: 18000, masters: 16000, mba: 35000 },
      private: { bachelors: 25000, masters: 22000, mba: 50000 },
    },
    living: 15000, // GIC requirement ~10,000 + additional
    livingLabel: 'GIC + Living Expenses',
    visa: 235,
    insurance: 800,
    flight: 900,
    extras: [
      { label: 'GIC Deposit', cost: 20635 },
      { label: 'Biometrics Fee', cost: 85 },
      { label: 'Medical Exam', cost: 200 },
    ],
    note: 'SDS (Student Direct Stream) requires GIC of CAD 20,635. Tuition varies by province — Quebec is often cheaper.',
  },
  australia: {
    name: 'Australia', flag: 'AU', currency: 'AUD', currencySymbol: 'A$', inrRate: 55,
    tuition: {
      public:  { bachelors: 25000, masters: 28000, mba: 45000 },
      private: { bachelors: 20000, masters: 22000, mba: 35000 },
    },
    living: 24505, // Financial capacity requirement per year
    livingLabel: 'Financial Capacity (visa requirement)',
    visa: 710,
    insurance: 600, // OSHC
    flight: 700,
    extras: [
      { label: 'OSHC (Health Cover)', cost: 600 },
      { label: 'Medical Exam', cost: 350 },
      { label: 'English Test (IELTS/PTE)', cost: 250 },
    ],
    note: 'Australia requires proof of AUD 24,505/year for living. OSHC (Overseas Student Health Cover) is mandatory for the full duration.',
  },
  ireland: {
    name: 'Ireland', flag: 'IE', currency: 'EUR', currencySymbol: '\u20AC', inrRate: 90,
    tuition: {
      public:  { bachelors: 12000, masters: 14000, mba: 25000 },
      private: { bachelors: 15000, masters: 18000, mba: 30000 },
    },
    living: 10000,
    livingLabel: 'Proof of Funds (visa requirement)',
    visa: 60,
    insurance: 500,
    flight: 550,
    extras: [
      { label: 'IRP Registration', cost: 300 },
      { label: 'Medical Insurance', cost: 500 },
      { label: 'English Test', cost: 220 },
    ],
    note: 'Ireland offers a 2-year stay-back visa (Third Level Graduate Programme). EU tuition rates do not apply to non-EU students.',
  },
};

const degreeOptions = [
  { value: 'bachelors', label: 'Bachelors' },
  { value: 'masters', label: 'Masters' },
  { value: 'mba', label: 'MBA' },
];

const institutionOptions = [
  { value: 'public', label: 'Public University' },
  { value: 'private', label: 'Private University' },
];

const durationOptions = [
  { value: 1, label: '1 Year' },
  { value: 2, label: '2 Years' },
  { value: 3, label: '3 Years' },
  { value: 4, label: '4 Years' },
];

function getFlagEmoji(code) {
  return String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt()));
}

function formatNum(n) {
  return n.toLocaleString('en-IN');
}

const BreakdownRow = ({ icon: Icon, label, local, inr, highlight }) => (
  <div className={`flex items-center justify-between py-3 px-4 rounded-lg ${highlight ? 'bg-[#003087] text-white' : 'bg-slate-50'}`}>
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 ${highlight ? 'text-[#C5A028]' : 'text-[#003087]'} shrink-0`} />
      <span className={`text-sm font-medium ${highlight ? 'text-white' : 'text-slate-700'}`}>{label}</span>
    </div>
    <div className="text-right">
      <div className={`text-sm font-bold ${highlight ? 'text-[#C5A028]' : 'text-slate-900'}`}>{local}</div>
      <div className={`text-xs ${highlight ? 'text-slate-300' : 'text-slate-500'}`}>{inr}</div>
    </div>
  </div>
);

const CostCalculatorPage = () => {
  usePageMeta(
    'Study Abroad Cost Calculator 2026 | Germany, UK, Canada, Australia',
    'Calculate your total study abroad cost — tuition, living, visa, flights & health insurance — for Germany, UK, Canada, Australia, Ireland & Dubai. Instant INR breakdown.',
  );
  const [country, setCountry] = useState('');
  const [degree, setDegree] = useState('');
  const [institution, setInstitution] = useState('');
  const [duration, setDuration] = useState('');
  const [calculated, setCalculated] = useState(false);

  const canCalculate = country && degree && institution && duration;

  const result = useMemo(() => {
    if (!canCalculate) return null;
    const c = costData[country];
    const d = Number(duration);
    const tuitionPerYear = c.tuition[institution]?.[degree] || 0;
    const totalTuition = tuitionPerYear * d;
    const totalLiving = c.living * d;
    const visaFee = c.visa;
    const totalInsurance = c.insurance * d;
    const flightCost = c.flight;
    const extrasTotal = c.extras.reduce((sum, e) => sum + e.cost, 0);
    const mandatoryTotal = visaFee + totalInsurance + flightCost + extrasTotal;
    const grandTotal = totalTuition + totalLiving + mandatoryTotal;
    const grandTotalINR = grandTotal * c.inrRate;

    return {
      country: c,
      duration: d,
      tuitionPerYear, totalTuition,
      totalLiving,
      visaFee, totalInsurance, flightCost, extrasTotal,
      mandatoryTotal,
      grandTotal, grandTotalINR,
    };
  }, [country, degree, institution, duration, canCalculate]);

  const handleCalculate = () => {
    if (canCalculate) setCalculated(true);
  };

  const c = country ? costData[country] : null;

  return (
    <div data-testid="sa-cost-calculator">
      {/* Hero */}
      <section className="sa-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-80 h-80 bg-[#C5A028] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Calculator className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Interactive Tool</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Study Abroad <span className="text-[#C5A028]">Cost Calculator</span>
          </h1>
          <p className="text-base text-slate-300 max-w-2xl mx-auto">
            Get an instant estimate of your total study abroad expenses including tuition,
            living costs, visa fees, and mandatory charges.
          </p>
        </div>
      </section>

      {/* Calculator Form */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 shadow-sm" data-testid="sa-cost-form">
            <h2 className="text-lg font-bold font-syne text-[#003087] mb-6">
              Configure Your Study Plan
            </h2>

            {/* Country */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">Country</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(costData).map(([key, val]) => (
                  <button
                    key={key} type="button"
                    onClick={() => { setCountry(key); setCalculated(false); }}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      country === key ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`cost-country-${key}`}
                  >
                    <div className="text-xl mb-1">{getFlagEmoji(val.flag)}</div>
                    <div className="text-xs font-semibold text-slate-900">{val.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Degree + Institution */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Degree Type</label>
                <div className="space-y-2">
                  {degreeOptions.map((opt) => (
                    <button
                      key={opt.value} type="button"
                      onClick={() => { setDegree(opt.value); setCalculated(false); }}
                      className={`w-full p-3 rounded-xl border-2 text-left text-sm font-medium transition-all ${
                        degree === opt.value ? 'border-[#003087] bg-[#003087]/5 text-[#003087]' : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                      data-testid={`cost-degree-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Institution Type</label>
                <div className="space-y-2">
                  {institutionOptions.map((opt) => (
                    <button
                      key={opt.value} type="button"
                      onClick={() => { setInstitution(opt.value); setCalculated(false); }}
                      className={`w-full p-3 rounded-xl border-2 text-left text-sm font-medium transition-all ${
                        institution === opt.value ? 'border-[#003087] bg-[#003087]/5 text-[#003087]' : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                      data-testid={`cost-inst-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Duration */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-700 mb-3">Duration</label>
              <div className="grid grid-cols-4 gap-3">
                {durationOptions.map((opt) => (
                  <button
                    key={opt.value} type="button"
                    onClick={() => { setDuration(opt.value); setCalculated(false); }}
                    className={`p-3 rounded-xl border-2 text-center text-sm font-semibold transition-all ${
                      duration === opt.value ? 'border-[#003087] bg-[#003087]/5 text-[#003087]' : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                    data-testid={`cost-duration-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleCalculate}
              disabled={!canCalculate}
              className="w-full bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3.5 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="sa-cost-calculate"
            >
              Calculate Total Cost
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      {calculated && result && (
        <section className="pb-16 lg:pb-24 bg-white" data-testid="sa-cost-results">
          <div className="max-w-3xl mx-auto px-6">
            {/* Grand Total Card */}
            <div className="bg-gradient-to-r from-[#003087] to-[#001F5C] rounded-2xl p-6 lg:p-8 mb-8 text-center">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Estimated Total Cost ({result.duration} {result.duration === 1 ? 'Year' : 'Years'})</p>
              <div className="text-4xl sm:text-5xl font-bold font-syne text-[#C5A028] mb-1">
                {c.currencySymbol} {formatNum(result.grandTotal)}
              </div>
              <div className="text-lg text-slate-300 font-semibold">
                ~ INR {formatNum(result.grandTotalINR)}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {getFlagEmoji(c.flag)} {c.name} &bull; {degreeOptions.find(d => d.value === degree)?.label} &bull; {institutionOptions.find(i => i.value === institution)?.label} &bull; {result.duration}yr
              </p>
            </div>

            {/* Detailed Breakdown */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 mb-8">
              <h3 className="text-base font-bold font-syne text-[#003087] mb-5">Cost Breakdown</h3>
              <div className="space-y-2.5">
                <BreakdownRow
                  icon={GraduationCap}
                  label={`Tuition Fees (${c.currencySymbol}${formatNum(result.tuitionPerYear)}/yr x ${result.duration}yr)`}
                  local={`${c.currencySymbol} ${formatNum(result.totalTuition)}`}
                  inr={`INR ${formatNum(result.totalTuition * c.inrRate)}`}
                />
                <BreakdownRow
                  icon={Home}
                  label={`${c.livingLabel} (${result.duration}yr)`}
                  local={`${c.currencySymbol} ${formatNum(result.totalLiving)}`}
                  inr={`INR ${formatNum(result.totalLiving * c.inrRate)}`}
                />

                <div className="pt-2 pb-1">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider px-4">Mandatory Costs</p>
                </div>

                <BreakdownRow
                  icon={FileText}
                  label="Visa Application Fee"
                  local={`${c.currencySymbol} ${formatNum(result.visaFee)}`}
                  inr={`INR ${formatNum(result.visaFee * c.inrRate)}`}
                />
                <BreakdownRow
                  icon={Heart}
                  label={`Health Insurance (${result.duration}yr)`}
                  local={`${c.currencySymbol} ${formatNum(result.totalInsurance)}`}
                  inr={`INR ${formatNum(result.totalInsurance * c.inrRate)}`}
                />
                <BreakdownRow
                  icon={Plane}
                  label="Average Flight (One-Way)"
                  local={`${c.currencySymbol} ${formatNum(result.flightCost)}`}
                  inr={`INR ${formatNum(result.flightCost * c.inrRate)}`}
                />

                {c.extras.map((ex) => (
                  <BreakdownRow
                    key={ex.label}
                    icon={CreditCard}
                    label={ex.label}
                    local={`${c.currencySymbol} ${formatNum(ex.cost)}`}
                    inr={`INR ${formatNum(ex.cost * c.inrRate)}`}
                  />
                ))}

                <div className="pt-2">
                  <BreakdownRow
                    icon={Shield}
                    label="GRAND TOTAL"
                    local={`${c.currencySymbol} ${formatNum(result.grandTotal)}`}
                    inr={`INR ${formatNum(result.grandTotalINR)}`}
                    highlight
                  />
                </div>
              </div>

              {c.note && (
                <p className="text-xs text-slate-500 mt-5 bg-slate-50 rounded-lg p-3 leading-relaxed">
                  <strong>Note:</strong> {c.note}
                </p>
              )}
            </div>

            {/* Lead Capture CTA */}
            <div className="bg-gradient-to-r from-[#003087] to-[#001F5C] rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold font-syne text-white mb-2">
                Download Detailed Budget Blueprint & Loan Options
              </h3>
              <p className="text-sm text-slate-300 mb-6 max-w-lg mx-auto">
                Get a personalized financial plan including education loan options,
                scholarship recommendations, and payment timeline.
              </p>
              <Link
                to={`/study-abroad/book-counselling/?country=${encodeURIComponent(c.name)}&degree=${degree}&budget=${result.grandTotal}`}
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all sa-pulse-cta"
                data-testid="sa-cost-cta"
              >
                Get Free Budget Blueprint <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-xs text-slate-400 mt-3">Free consultation with our finance experts</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CostCalculatorPage;
